import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useContentOverrides } from '../../lib/useContentOverrides';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BlogPage() {
  const c = useContentOverrides('journal');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchParams, setSearchParams] = useSearchParams();
  const tagFilter = searchParams.get('tag');

  useEffect(() => {
    axios.get(`${API}/api/cms/journal/published`)
      .then(res => setPosts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const cats = new Set(posts.map(p => p.category).filter(Boolean));
    return ['All', ...Array.from(cats).sort()];
  }, [posts]);

  // Apply both category filter AND tag filter (if either active). Tag filter
  // does a case-insensitive match against the entry's tags array.
  const filtered = useMemo(() => {
    let out = activeCategory === 'All' ? posts : posts.filter(p => p.category === activeCategory);
    if (tagFilter) {
      const tNorm = tagFilter.toLowerCase();
      out = out.filter(p => Array.isArray(p.tags) && p.tags.some(t => (t || '').toLowerCase() === tNorm));
    }
    return out;
  }, [posts, activeCategory, tagFilter]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  const clearTag = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('tag');
    setSearchParams(params, { replace: true });
  };

  return (
    <div data-testid="page-blog">
      {c.previewMode && (
        <div className="bg-[#1A1A1A] text-white text-center py-2 px-4 text-[11px] tracking-[0.22em] uppercase font-mono" data-testid="blog-preview-banner">
          Studio draft preview &mdash; not yet published
        </div>
      )}
      {/* Hero — editorial, harmonized with the rest of the public surface */}
      <section className="pt-20 pb-12 sm:pt-32 sm:pb-20 px-6 text-center" data-testid="blog-hero">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-6">{c.get('hero.kicker', 'The Journal')}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] max-w-2xl mx-auto leading-tight">
          {c.get('hero.title', 'Stories & Insights')}
        </h1>
        <div className="w-12 h-px bg-[#E8E4DC] mx-auto my-6 sm:my-8" aria-hidden="true" />
        <p className="font-accent italic text-base sm:text-lg text-[#6B6B6B] max-w-xl mx-auto leading-relaxed">
          {c.get('hero.subtitle', 'Travel inspiration, destination guides, and advisor perspectives.')}
        </p>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-t border-[#E8E4DC] py-8 sm:py-10 px-6" data-testid="blog-categories">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-2">
          {/* Active tag chip — only shown when ?tag= is present */}
          {tagFilter && (
            <button
              onClick={clearTag}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#C9A961] bg-[#C9A961]/10 text-[#1A1A1A] hover:bg-[#C9A961]/20 transition-colors mr-2"
              data-testid="blog-active-tag"
              title="Click to clear tag filter"
            >
              <span>#{tagFilter}</span>
              <span className="text-[#999]">·</span>
              <span className="text-[#999] text-[10px] uppercase tracking-[0.15em]">clear</span>
            </button>
          )}
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${activeCategory === cat ? 'border-[#1A1A1A] text-[#1A1A1A] bg-white' : 'border-[#E8E4DC] text-[#6B6B6B] hover:border-[#1A1A1A] hover:text-[#1A1A1A]'}`}
              data-testid={`blog-cat-${cat.toLowerCase().replace(/\s/g, '-')}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Post List */}
      <section className="bg-white py-10 sm:py-16 px-6" data-testid="blog-list">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <p className="text-sm text-[#999] text-center py-12">Loading...</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-sm text-[#999]">
                {tagFilter ? <>No entries tagged <em>#{tagFilter}</em>.</> : 'No entries yet. Check back soon.'}
              </p>
              {tagFilter && (
                <button
                  onClick={clearTag}
                  className="mt-3 text-xs text-[#1A1A1A] underline decoration-[#C9A961]/40 underline-offset-4 hover:decoration-[#C9A961]"
                >
                  Clear tag filter
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {featured && (
                <Link to={`/blog/${featured.slug}`} className="block mb-16 grid grid-cols-1 md:grid-cols-2 gap-10 group" data-testid="blog-featured-post">
                  {featured.cover_image_url ? (
                    <img src={featured.cover_image_url.startsWith('http') ? featured.cover_image_url : `${API}${featured.cover_image_url}`} alt={featured.title} className="aspect-[4/3] w-full object-cover rounded" />
                  ) : (
                    <div className="aspect-[4/3] bg-[#F0EDE6] rounded" />
                  )}
                  <div className="flex flex-col justify-center">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-[#C9A961] mb-3">{featured.category}</p>
                    <h2 className="font-serif text-lg text-[#1A1A1A] mb-4 group-hover:underline">{featured.title}</h2>
                    {featured.subtitle && <p className="text-sm text-[#6B6B6B] mb-4 leading-relaxed">{featured.subtitle}</p>}
                    {featured.published_at && (
                      <p className="text-xs text-[#999]">{new Date(featured.published_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
                    )}
                  </div>
                </Link>
              )}

              {/* Post Grid */}
              {rest.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                  {rest.map(post => (
                    <Link to={`/blog/${post.slug}`} key={post.id} className="group" data-testid={`blog-post-${post.slug}`}>
                      {post.cover_image_url ? (
                        <img src={post.cover_image_url.startsWith('http') ? post.cover_image_url : `${API}${post.cover_image_url}`} alt={post.title} className="aspect-[4/3] w-full object-cover rounded mb-4" />
                      ) : (
                        <div className="aspect-[4/3] bg-[#F0EDE6] rounded mb-4" />
                      )}
                      <p className="text-[11px] uppercase tracking-[0.22em] text-[#C9A961] mb-2">{post.category}</p>
                      <h3 className="font-serif text-sm text-[#1A1A1A] mb-2 group-hover:underline">{post.title}</h3>
                      {post.subtitle && <p className="text-xs text-[#6B6B6B] mb-2 leading-relaxed">{post.subtitle}</p>}
                      {post.published_at && (
                        <p className="text-xs text-[#999]">{new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA — Filled Black */}
      <section className="py-16 sm:py-24 px-6 text-center bg-[#F5F3EE] border-t border-[#E8E4DC]" data-testid="blog-cta">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-6">{c.get('cta.kicker', 'A World Apart')}</p>
        <p className="font-accent text-base sm:text-lg text-[#1A1A1A] italic mb-10">
          {c.get('cta.message', 'Inspired? Let us know what resonates.')}
        </p>
        <Link
          to="/plan"
          className="px-8 py-3.5 bg-[#1A1A1A] text-white text-sm tracking-wide rounded hover:bg-[#333] transition-colors"
          data-testid="blog-cta-button"
        >
          {c.get('cta.button_label', 'Begin Your Experience')}
        </Link>
      </section>
    </div>
  );
}
